import { useCookie, useRuntimeConfig } from '#imports'
import { ref, watch } from 'vue'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { DIDOMI_COOKIE_NAME, VENDORS_ID } from '../didomi/config'

export function useDataConsent() {
  const { market } = useMarketplace()
  const {
    tracking: { didomi, countriesWithNoCookieOptIn },
  } = useRuntimeConfig().public
  const cookie = useCookie<string | null>(DIDOMI_COOKIE_NAME)

  const logger = useLogger()
  const isDidomiEnabled =
    didomi.isEnabled && didomi.enabledCountryCodes.includes(market.countryCode)

  // Helper function to determine if modal should be displayed based on country and cookie state
  // This logic is used both for initial SSR state and client-side updates
  const shouldDisplayModal = (hasConsentCookie: boolean) => {
    return (
      !countriesWithNoCookieOptIn.includes(market.countryCode) &&
      !hasConsentCookie
    )
  }

  // Server-side modal display logic: Show modal if country requires cookie consent AND no consent cookie exists
  // This determines the initial SSR state - the modal will be hidden client-side if user has already consented
  const shouldShowModalServerSide = shouldDisplayModal(!!cookie.value)

  const isCookiesModalDisplayed = ref(shouldShowModalServerSide)

  // Client-side watcher: Update modal visibility when consent cookie changes
  // This handles the case where user accepts/declines cookies after page load
  watch(
    cookie,
    () => {
      isCookiesModalDisplayed.value = shouldDisplayModal(!!cookie.value)
    },
    { immediate: false },
  )

  function showCookiesModal() {
    if (!process.client) return

    window.Didomi?.preferences.show()
  }

  function acceptAll() {
    if (!process.client) return

    window.Didomi?.setUserAgreeToAll()
  }

  function declineAll() {
    if (!process.client) return

    window.Didomi?.setUserDisagreeToAll()
  }

  function getUserConsentForVendor(vendor: string): boolean {
    try {
      if (!isDidomiEnabled || !process.client || !window.Didomi) return false

      return window.Didomi.getCurrentUserStatus().vendors[vendor].enabled
    } catch (error) {
      logger.error(`[Didomi] Error getting user consent for vendor ${vendor}`, {
        error: error as Error,
        owners: ['bot-group-onboard-front'],
      })

      return false
    }
  }

  function getUserConsentForAmplitude() {
    return getUserConsentForVendor(VENDORS_ID.amplitude)
  }

  return {
    acceptAll,
    declineAll,
    showCookiesModal,
    getUserConsentForAmplitude,
    isDidomiEnabled,
    isCookiesModalDisplayed,
  }
}
